<template>
  <div class="user-credit-cards">
    <b-card :title="$t('user_credit_cards.title')" class="mb-2">
      <p class="text-muted mb-3">{{ $t('user_credit_cards.intro') }}</p>

      <div class="flex flex-wrap gap-3 items-end mb-4">
        <div class="w-full sm:w-auto flex-grow select-suggestion-user">
          <select-suggestion
            column="name,email"
            model="User"
            v-model="selectedUser"
            :label="$t('user_credit_cards.select_user')"
            :appendClearOption="true"
            :placeholderText="$t('user_credit_cards.user_placeholder')"
            :itemTextFormat="formatUserNameEmailHtml"
            dropdown-options-class="user-suggest-dropdown"
          />
        </div>
      </div>

      <b-alert variant="info" show class="p-1 mb-4">
        <small>{{ $t('user_credit_cards.register_in_marketplace') }}</small>
      </b-alert>

      <div v-if="!selectedUser" class="text-muted py-3">
        {{ $t('user_credit_cards.pick_user_hint') }}
      </div>

      <div v-else-if="loading" class="py-4 text-center">
        {{ $t('common.loading') }}
      </div>

      <div v-else-if="!cards.length" class="text-muted py-3">
        {{ $t('user_credit_cards.empty') }}
      </div>

      <div v-else class="overflow-x-auto">
        <table class="table b-table table-striped">
          <thead>
            <tr>
              <th>{{ $t('user_credit_cards.col_id') }}</th>
              <th>{{ $t('user_credit_cards.col_number') }}</th>
              <th>{{ $t('user_credit_cards.col_brand') }}</th>
              <th>{{ $t('user_credit_cards.col_holder') }}</th>
              <th>{{ $t('user_credit_cards.col_expiry') }}</th>
              <th>{{ $t('user_credit_cards.col_gateway') }}</th>
              <th>{{ $t('user_credit_cards.col_token') }}</th>
              <th>{{ $t('user_credit_cards.col_default') }}</th>
              <th width="120" />
            </tr>
          </thead>
          <tbody>
            <tr v-for="c in cards" :key="c.id">
              <td>{{ c.id }}</td>
              <td>{{ c.masked_number }}</td>
              <td>{{ c.brand_label || c.brand }}</td>
              <td>{{ c.holder_name }}</td>
              <td>{{ c.expiry_date }}</td>
              <td>{{ c.gateway || '—' }}</td>
              <td>
                <vs-chip v-if="c.is_tokenized" color="success" transparent class="text-sm">{{ $t('user_credit_cards.yes') }}</vs-chip>
                <span v-else class="text-muted">—</span>
              </td>
              <td>
                <vs-chip v-if="c.is_default" color="primary" transparent class="text-sm">{{ $t('user_credit_cards.yes') }}</vs-chip>
                <span v-else class="text-muted">—</span>
              </td>
              <td class="text-nowrap">
                <feather-icon
                  v-if="!c.is_default"
                  v-permission="'subscriptions.edit'"
                  icon="StarIcon"
                  class="h-5 w-5 stroke-current text-primary cursor-pointer align-middle mr-1"
                  v-tooltip.top="$t('user_credit_cards.set_default')"
                  @click="setDefault(c)"
                />
                <feather-icon
                  v-permission="'subscriptions.delete'"
                  icon="Trash2Icon"
                  class="h-5 w-5 stroke-current text-danger cursor-pointer align-middle"
                  v-tooltip.top="$t('action.delete')"
                  @click="confirmRemove(c)"
                />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </b-card>
  </div>
</template>

<script>
import { BAlert, BCard } from 'bootstrap-vue'
import SelectSuggestion from '@/components/SelectSuggestion.vue'
import { formatUserNameEmailHtml } from '@/util/suggestionLabelFormat'
import UserCreditCardService from '@/services/api/UserCreditCardService'

/**
 * Consulta e manutenção dos cartões de um usuário. Não cadastra: os dados do cartão
 * são criptografados no navegador do titular com o SDK do PagBank, então o cadastro
 * só acontece no marketplace, onde o próprio dono do cartão os informa.
 */
export default {
  name: 'UserCreditCardsIndex',
  components: {
    BAlert,
    BCard,
    SelectSuggestion
  },
  data() {
    return {
      service: null,
      selectedUser: null,
      cards: [],
      loading: false
    }
  },
  created() {
    this.service = UserCreditCardService.build(this.$vs)
  },
  watch: {
    selectedUser(val) {
      this.cards = []
      if (val && val.id) {
        this.fetchCards()
      }
    }
  },
  methods: {
    formatUserNameEmailHtml,
    fetchCards() {
      if (!this.selectedUser || !this.selectedUser.id) return
      this.loading = true
      this.service
        .list(this.selectedUser.id)
        .then((rows) => {
          this.cards = Array.isArray(rows) ? rows : []
        })
        .catch(() => {
          this.cards = []
          this.$vs.notify({
            title: this.$t('messages.error'),
            text: this.$t('user_credit_cards.load_error'),
            color: 'danger',
            position: 'top-right'
          })
        })
        .finally(() => {
          this.loading = false
        })
    },
    setDefault(card) {
      if (!this.selectedUser || !this.selectedUser.id) return
      this.service
        .setDefault(this.selectedUser.id, card.id)
        .then(() => {
          this.$vs.notify({
            title: this.$t('messages.success'),
            text: this.$t('user_credit_cards.default_updated'),
            color: 'success',
            position: 'top-right'
          })
          this.fetchCards()
        })
        .catch(() => {
          this.$vs.notify({
            title: this.$t('messages.error'),
            text: this.$t('user_credit_cards.default_error'),
            color: 'danger',
            position: 'top-right'
          })
        })
    },
    confirmRemove(card) {
      this.$bvModal
        .msgBoxConfirm(this.$t('user_credit_cards.confirm_delete'), {
          title: this.$t('action.delete'),
          okVariant: 'danger',
          okTitle: this.$t('action.confirm'),
          cancelTitle: this.$t('action.cancel'),
          centered: true,
          footerClass: 'flex-row-reverse justify-content-end'
        })
        .then(ok => {
          if (ok) this.removeCard(card)
        })
    },
    removeCard(card) {
      this.service
        .deleteCard(this.selectedUser.id, card.id)
        .then(() => {
          this.$vs.notify({
            title: this.$t('messages.success'),
            text: this.$t('user_credit_cards.deleted_ok'),
            color: 'success',
            position: 'top-right'
          })
          this.fetchCards()
        })
        .catch(() => {
          this.$vs.notify({
            title: this.$t('messages.error'),
            text: this.$t('user_credit_cards.delete_error'),
            color: 'danger',
            position: 'top-right'
          })
        })
    }
  }
}
</script>

<!-- Lista: _fixesVuesax.scss (painel no body) -->
<style lang="scss" scoped>
.select-suggestion-user {
  min-width: 260px;
}
</style>
